import { Project, ProjectInvoice } from '../types';

/**
 * Facturas que un cliente puede ver en su portal.
 *
 * Vive fuera de la vista y con pruebas porque encierra una regla que no se
 * puede romper por descuido: **el BORRADOR nunca sale de la empresa**. Un
 * borrador es el documento de trabajo del contratista — puede llevar cifras a
 * medias, líneas de prueba o un total que todavía va a cambiar. Que se filtre
 * al portal no es un fallo visual: es el cliente leyendo un precio que nadie le
 * ha ofrecido.
 *
 * (Esta pantalla nació con facturas inventadas escritas a mano —un saldo
 * vencido de $1,250 que se le enseñaba a clientes reales— y por eso la lógica
 * ahora está aquí, medida y probada.)
 */
export function facturasDelCliente(projects: Project[], clientId?: string | null): ProjectInvoice[] {
    if (!clientId) return [];
    return projects
        .filter(p => p.clientId === clientId)
        .flatMap(p => p.invoices ?? [])
        .filter(inv => inv.status !== 'Draft');
}

/** Emitidas y sin cobrar, las más urgentes primero. */
export function facturasPorPagar(facturas: ProjectInvoice[]): ProjectInvoice[] {
    return facturas
        .filter(i => i.status === 'Sent' || i.status === 'Overdue')
        .sort((a, b) => fecha(a.dueDate || a.date) - fecha(b.dueDate || b.date));
}

/** Ya cobradas, las más recientes primero. */
export function facturasPagadas(facturas: ProjectInvoice[]): ProjectInvoice[] {
    return facturas
        .filter(i => i.status === 'Paid')
        .sort((a, b) => fecha(b.paidAt || b.date) - fecha(a.paidAt || a.date));
}

/** Lo que el cliente debe hoy. */
export function saldoPendiente(facturas: ProjectInvoice[]): number {
    return facturasPorPagar(facturas).reduce((suma, i) => suma + (i.amount || 0), 0);
}

/** Fecha ordenable. Una fecha corrupta va al final en vez de reventar el orden. */
function fecha(iso?: string): number {
    const t = iso ? new Date(iso).getTime() : NaN;
    return Number.isNaN(t) ? 0 : t;
}
